import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { LuActivity } from "react-icons/lu";
import { FiSearch } from "react-icons/fi";

import ListPageShell from "../list-page/ListPageShell";
import ListPageHeader from "../list-page/ListPageHeader";
import ListPageSearch from "../list-page/ListPageSearch";
import ListPageEmpty from "../list-page/ListPageEmpty";
import ListPager from "../list-page/ListPager";

import AnalyticsTable from "./AnalyticsTable";
import AuditLogView from "./AuditLogView";
import ReactModal from "../ReactModal";

import { useGetAuditLogsQuery } from "../../store/services/auditLogServices";
import { auditLogViewActions } from "../../store/auditLogViewSlice";

const PAGE_LIMIT = 10;

export default function AnalyticsMain() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isOpen: isAuditOpen } = useSelector((s) => s.audit_log_view);

  const [filteredLogs, setFilteredLogs] = useState([]);
  const [pages, setPages] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [params, setParams] = useState({});
  const [searchTextInput, setSearchTextInput] = useState("");
  const [query] = useState(new URLSearchParams(window.location.search));

  const { data, isLoading, isFetching } = useGetAuditLogsQuery({ params });

  /* Hydrate from URL */
  useEffect(() => {
    const newParams = {};
    query.forEach((v, k) => (newParams[k] = v));
    setParams(newParams);
    if (query.has("page")) setCurrentPage(parseInt(query.get("page")));
    if (query.has("q")) setSearchTextInput(query.get("q"));
  }, [query]);

  /* Sync data */
  useEffect(() => {
    if (data) {
      setFilteredLogs(data?.data || data?.data?.data || []);
      setPages({
        currentPage: data?.currentPage ?? data?.data?.currentPage,
        totalPages: data?.totalPages ?? data?.data?.totalPages,
        totalCount: data?.totalCount ?? data?.data?.totalCount,
      });
    }
  }, [data]);

  /* Reflect params into URL */
  useEffect(() => {
    const urlParams = new URLSearchParams(params).toString();
    navigate(`/audit_logs?${urlParams}`, { replace: true });
  }, [params, navigate]);

  /* Search debounce */
  const debounceRef = useRef(null);
  const handleSearchTextInputChange = (e) => {
    const text = e.target.value;
    setSearchTextInput(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (text.length > 2) {
      debounceRef.current = setTimeout(() => {
        setParams((p) => ({ ...p, q: text, page: 1 }));
        setCurrentPage(1);
      }, 400);
    } else {
      setParams((p) => {
        const { q, ...rest } = p;
        return rest;
      });
    }
  };

  const handleSort = (value) => {
    setParams((p) => {
      if (p.sort_by === value && p.sort_order === "asc") {
        const { sort_by, sort_order, ...rest } = p;
        return rest;
      }
      return {
        ...p,
        sort_by: value,
        sort_order: p.sort_by === value && p.sort_order === "desc" ? "asc" : "desc",
      };
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setPages((p) => ({ ...p, currentPage: page }));
    setParams((p) => ({ ...p, page }));
  };

  const handleRowClick = (log) => {
    dispatch(auditLogViewActions.openModal(log));
  };

  const hasFilters = !!params.q;

  const handleClearFilters = () => {
    setSearchTextInput("");
    setCurrentPage(1);
    setParams({});
  };

  const isEmpty = !isLoading && !isFetching && filteredLogs.length === 0;

  const totalCount = pages?.totalCount ?? 0;
  const rangeLabel = useMemo(() => {
    if (!totalCount) return "";
    const start = (currentPage - 1) * PAGE_LIMIT + 1;
    const end = Math.min(currentPage * PAGE_LIMIT, totalCount);
    return `Po shfaqen ${start}–${end} prej ${totalCount.toLocaleString()}`;
  }, [currentPage, totalCount]);

  return (
    <>
      <ReactModal />
      {isAuditOpen && <AuditLogView />}

      <ListPageShell>
        <ListPageHeader
          eyebrow={{ icon: <LuActivity size={18} />, label: "Audit logs" }}
          title="Historiku i veprimeve"
          subtitle={
            totalCount
              ? `${totalCount.toLocaleString()} regjistrime`
              : "Historiku i veprimeve të bëra në sistem"
          }
        />

        <ListPageSearch
          value={searchTextInput}
          onChange={handleSearchTextInputChange}
          placeholder="Kërko sipas përdoruesit, veprimit ose tabelës..."
          hasFilters={hasFilters}
          onClearFilters={handleClearFilters}
        />

        {isEmpty ? (
          <ListPageEmpty
            icon={hasFilters ? FiSearch : LuActivity}
            title={hasFilters ? "Asnjë regjistrim nuk u gjet" : "Ende asnjë regjistrim"}
            subtitle={
              hasFilters
                ? "Provoni të ndryshoni ose të pastroni filtrat për të parë më shumë rezultate."
                : "Veprimet e bëra në sistem do të shfaqen këtu."
            }
            action={
              hasFilters && (
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition"
                >
                  Pastro filtrat
                </button>
              )
            }
          />
        ) : (
          <AnalyticsTable
            logs={filteredLogs}
            isLoading={isLoading || isFetching}
            params={params}
            onSort={handleSort}
            onRowClick={handleRowClick}
          />
        )}

        {!isEmpty && (
          <ListPager
            currentPage={currentPage}
            totalPages={pages?.totalPages || 1}
            onChange={handlePageChange}
            rangeLabel={rangeLabel}
          />
        )}
      </ListPageShell>
    </>
  );
}
